/**
 * feed.ts — EXTERNAL content aggregator for the Feed panel.
 *
 * This is the *external world* surface (Reddit / RSS / YouTube / Bluesky), kept
 * deliberately DISTINCT from the internal Timeline (SAGE/client data via
 * sage-intel-mcp). The CSV journal is explicit about the separation and about
 * the prior sin: "Feed shows hardcoded seed items as if real." So everything
 * here is fetched live.
 *
 * Transport: the Rust `http_get` command (reqwest) — the WebView cannot fetch
 * reddit.com / arbitrary RSS (CORS), but Rust can. In `npm run dev` (plain
 * browser) external fetch is unsupported and the panel says so.
 */

import { isTauri } from "./settings";

export type FeedKind = "reddit" | "rss" | "youtube" | "bluesky";

export interface FeedSource {
  id: string;
  kind: FeedKind;
  label: string;
  /** Subreddit name (reddit), feed URL (rss/youtube), or handle (bluesky). */
  ref: string;
  enabled: boolean;
}

export interface FeedItem {
  id: string;
  sourceId: string;
  sourceLabel: string;
  kind: FeedKind;
  title: string;
  url: string;
  author?: string;
  ts: number; // epoch ms; 0 if unknown
  score?: number; // reddit ups
  comments?: number; // reddit comment count
  thumbnail?: string;
  summary?: string;
}

const SOURCES_KEY = "sage_feed_sources";

/** Sensible defaults — Evan's worlds: restaurants, local Cape Cod, AI/tech, news.
 *  Reddit is fetched via its Atom .rss endpoint (the public JSON API 403s
 *  non-OAuth clients; plain /r/<sub>/.rss returns 200). */
export const DEFAULT_SOURCES: FeedSource[] = [
  { id: "r-restaurateur", kind: "reddit", label: "r/restaurateur", ref: "restaurateur", enabled: true },
  { id: "r-kitchenconfidential", kind: "reddit", label: "r/KitchenConfidential", ref: "KitchenConfidential", enabled: true },
  { id: "r-smallbusiness", kind: "reddit", label: "r/smallbusiness", ref: "smallbusiness", enabled: true },
  { id: "r-localllama", kind: "reddit", label: "r/LocalLLaMA", ref: "LocalLLaMA", enabled: true },
  { id: "eater", kind: "rss", label: "Eater", ref: "https://www.eater.com/rss/index.xml", enabled: true },
  { id: "hn", kind: "rss", label: "Hacker News", ref: "https://hnrss.org/frontpage", enabled: true },
  { id: "r-capecod", kind: "reddit", label: "r/capecod", ref: "capecod", enabled: false },
];

export function getSources(): FeedSource[] {
  try {
    const raw = localStorage.getItem(SOURCES_KEY);
    if (!raw) return DEFAULT_SOURCES;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed as FeedSource[];
    return DEFAULT_SOURCES;
  } catch {
    return DEFAULT_SOURCES;
  }
}

export function setSources(sources: FeedSource[]): void {
  try {
    localStorage.setItem(SOURCES_KEY, JSON.stringify(sources));
  } catch {
    /* storage unavailable */
  }
}

interface RawGet {
  status: number;
  contentType: string;
  body: string;
}

async function rawGet(url: string, timeoutMs = 12_000): Promise<RawGet> {
  if (!isTauri()) throw new Error("web-unsupported");
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<RawGet>("http_get", { url, timeoutMs });
}

function urlForSource(s: FeedSource): string {
  switch (s.kind) {
    case "reddit":
      // Atom feed — the JSON API 403s; /hot/.rss 429s; plain /.rss is stable.
      return `https://www.reddit.com/r/${encodeURIComponent(s.ref)}/.rss`;
    case "youtube":
    case "rss":
    case "bluesky":
      return s.ref;
  }
}

/** Reddit JSON parser — retained for a future OAuth path (the public JSON API
 *  currently 403s non-authenticated clients, so the live Feed uses .rss). */
export function parseReddit(body: string, s: FeedSource): FeedItem[] {
  const json = JSON.parse(body) as {
    data?: { children?: Array<{ data?: Record<string, unknown> }> };
  };
  const children = json.data?.children ?? [];
  const out: FeedItem[] = [];
  for (const c of children) {
    const d = c.data;
    if (!d) continue;
    if (d.stickied === true) continue;
    const permalink = typeof d.permalink === "string" ? `https://www.reddit.com${d.permalink}` : "";
    const thumb = typeof d.thumbnail === "string" && d.thumbnail.startsWith("http") ? d.thumbnail : undefined;
    out.push({
      id: `${s.id}:${String(d.id)}`,
      sourceId: s.id,
      sourceLabel: s.label,
      kind: "reddit",
      title: String(d.title ?? "(untitled)"),
      url: permalink || String(d.url ?? ""),
      author: typeof d.author === "string" ? `u/${d.author}` : undefined,
      ts: typeof d.created_utc === "number" ? d.created_utc * 1000 : 0,
      score: typeof d.ups === "number" ? d.ups : undefined,
      comments: typeof d.num_comments === "number" ? d.num_comments : undefined,
      thumbnail: thumb,
      summary: typeof d.selftext === "string" ? d.selftext.slice(0, 280) : undefined,
    });
  }
  return out;
}

function parseXmlFeed(body: string, s: FeedSource): FeedItem[] {
  const doc = new DOMParser().parseFromString(body, "application/xml");
  if (doc.querySelector("parsererror")) return [];
  const out: FeedItem[] = [];
  // RSS <item> or Atom <entry>
  const nodes = [
    ...Array.from(doc.querySelectorAll("item")),
    ...Array.from(doc.querySelectorAll("entry")),
  ];
  for (const n of nodes.slice(0, 25)) {
    const get = (tag: string) => n.querySelector(tag)?.textContent?.trim() ?? "";
    const title = get("title");
    // Atom links live in <link href>; RSS in <link> text.
    let link = get("link");
    const linkEl = n.querySelector("link");
    if (!link && linkEl?.getAttribute("href")) link = linkEl.getAttribute("href") ?? "";
    const dateStr = get("pubDate") || get("published") || get("updated") || get("dc\\:date");
    const ts = dateStr ? Date.parse(dateStr) : 0;
    const desc = (get("description") || get("summary") || get("content")).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 280);
    // Atom: <author><name>…</name></author>; RSS: <author> or <dc:creator>.
    const author = (n.querySelector("author > name")?.textContent
      || get("dc\\:creator")
      || n.querySelector("author")?.textContent
      || "").trim() || undefined;
    out.push({
      id: `${s.id}:${link || title}`,
      sourceId: s.id,
      sourceLabel: s.label,
      kind: s.kind,
      title: title || "(untitled)",
      url: link,
      author: author || undefined,
      ts: Number.isNaN(ts) ? 0 : ts,
      summary: desc || undefined,
    });
  }
  return out;
}

export interface SourceResult {
  source: FeedSource;
  items: FeedItem[];
  error?: string;
}

async function fetchSource(s: FeedSource): Promise<SourceResult> {
  try {
    const { status, body } = await rawGet(urlForSource(s));
    if (status === 429) return { source: s, items: [], error: "rate-limited (429)" };
    if (status >= 400) return { source: s, items: [], error: `HTTP ${status}` };
    // Reddit JSON API 403s non-OAuth; we fetch its Atom .rss, so all kinds parse as XML.
    // (parseReddit kept for a future authenticated path.)
    const items = parseXmlFeed(body, s);
    return { source: s, items };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { source: s, items: [], error: msg === "web-unsupported" ? "desktop-only" : msg };
  }
}

export interface FeedLoad {
  items: FeedItem[];
  perSource: SourceResult[];
  supported: boolean;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Fetch all enabled sources, merge + sort newest-first. RSS hosts go in
 * parallel, but Reddit rate-limits unauthenticated IPs hard (~1 req / 2s) and
 * 429s a 4-wide parallel burst — so Reddit sources are fetched sequentially
 * with a gap. 429s that still slip through surface as a visible per-source
 * error (never silent).
 */
export async function loadFeed(): Promise<FeedLoad> {
  if (!isTauri()) return { items: [], perSource: [], supported: false };
  const enabled = getSources().filter((s) => s.enabled);
  const reddit = enabled.filter((s) => s.kind === "reddit");
  const others = enabled.filter((s) => s.kind !== "reddit");

  const otherResults = Promise.all(others.map(fetchSource));
  const redditResults: SourceResult[] = [];
  for (const s of reddit) {
    redditResults.push(await fetchSource(s));
    if (s !== reddit[reddit.length - 1]) await sleep(800);
  }
  const perSource = [...(await otherResults), ...redditResults];
  const items = perSource.flatMap((r) => r.items).sort((a, b) => b.ts - a.ts);
  return { items, perSource, supported: true };
}

export function relTime(ts: number): string {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const m = Math.round(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}
